// Welle 64 (SIJ-666): Dateien aus der Weboberfläche herausziehen + Teilen.
//
// Chrome/Edge unterstützen dataTransfer.setData("DownloadURL", "<mime>:<name>:<url>"):
// die Datei landet beim Ablegen (Finder/Desktop/WhatsApp Web …) als echter Download.
// Wichtig: Der Browser lädt die URL OHNE eigene Header — deshalb hängt der
// maToken als ?token= an der URL (get_current_ma akzeptiert Query-Token).
import { API_BASE } from "../api/config";

const MIME_MAP = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  heic: "image/heic",
  webp: "image/webp",
  mp4: "video/mp4",
  mov: "video/quicktime",
  csv: "text/csv",
  txt: "text/plain",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  zip: "application/zip",
};

export function guessMime(filename) {
  const ext = (filename || "").split(".").pop().toLowerCase();
  return MIME_MAP[ext] || "application/octet-stream";
}

// Download-URL mit Query-Token (für DownloadURL-Drag und <a download>)
export function downloadUrl(path) {
  const token = localStorage.getItem("maToken") || "";
  return (
    `${API_BASE}/zuordnen/api/verschieben/file-download` +
    `?path=${encodeURIComponent(path)}&token=${encodeURIComponent(token)}`
  );
}

// Hotfix 6.9.1: Safari kennt „DownloadURL" nicht (das ist Chrome/Edge). Safari nimmt beim Ziehen aber eine
// fertige Datei an (dataTransfer.items.add(File)) — die muss beim Loslegen des Ziehens schon geladen sein.
// Deshalb lädt die Seite in Safari die Datei vor, sobald die Maus über der Kachel ist.
export const IST_SAFARI = typeof navigator !== "undefined"
  && /safari/i.test(navigator.userAgent) && !/chrome|chromium|crios|edg|android/i.test(navigator.userAgent);

const _dateien = new Map();   // path → File (fertig geladen)
const _laden = new Map();     // path → Promise<File>

export function dateiVorladen(path, filename) {
  if (!path || _dateien.has(path) || _laden.has(path)) return _laden.get(path);
  const name = filename || path.split("/").pop() || "datei";
  const p = fetch(downloadUrl(path))
    .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
    .then((blob) => {
      const f = new File([blob], name, { type: guessMime(name) });
      _dateien.set(path, f);
      if (_dateien.size > 40) _dateien.delete(_dateien.keys().next().value);   // nicht unbegrenzt behalten
      return f;
    })
    .catch((e) => { console.warn("[dragOut] Vorladen fehlgeschlagen:", e); })
    .finally(() => _laden.delete(path));
  _laden.set(path, p);
  return p;
}

// Datei in den Zieh-Vorgang legen: Chrome/Edge per DownloadURL (lädt beim Ablegen), Safari als fertige Datei.
// Gibt false zurück, wenn in Safari die Datei noch nicht geladen ist.
export function dateiInsZiehen(e, path, filename) {
  const name = filename || path.split("/").pop() || "datei";
  e.dataTransfer.effectAllowed = "copy";
  if (IST_SAFARI) {
    const f = _dateien.get(path);
    if (!f) { dateiVorladen(path, name); return false; }
    // NUR die Datei — keine URL/Text dazu (SIJ-666: sonst kommt das Dokument doppelt an)
    e.dataTransfer.items.add(f);
    return true;
  }
  const abs = new URL(downloadUrl(path), window.location.origin).href;
  // SIJ-666: NUR DownloadURL setzen. Zusätzliche text/uri-list + text/plain
  // führten dazu, dass WhatsApp die Datei UND die URL als zweiten Anhang
  // übernahm → Dokument kam doppelt an (Mail nahm nur die Datei).
  e.dataTransfer.setData("DownloadURL", `${guessMime(name)}:${name}:${abs}`);
  return true;
}

// Props zum Spreaden auf ein Datei-Element: {...dragOutProps(d.path, d.name)}
export function dragOutProps(path, filename) {
  if (!path) return {};
  const name = filename || path.split("/").pop() || "datei";
  return {
    draggable: true,
    title: "Zum Herausziehen greifen (Rechtsklick: Teilen/Download)",
    onMouseEnter: () => { if (IST_SAFARI) dateiVorladen(path, name); },
    onDragStart: (e) => {
      if (!dateiInsZiehen(e, path, name)) e.preventDefault();
    },
  };
}

// Hotfix 6.9.1: die GANZE Datei-Kachel ist Ziehgriff. Aus Eingabefeldern, Kästchen, Auswahllisten und
// Textfeldern heraus startet kein Ziehen (dort bleibt Markieren/Bedienen normal).
const _NICHT_ZIEHEN = "input, textarea, select, option, [contenteditable='true'], .v-no-drag";

export function kachelDragProps(path, filename) {
  if (!path) return {};
  const name = filename || path.split("/").pop() || "datei";
  return {
    draggable: true,
    onMouseEnter: () => { if (IST_SAFARI) dateiVorladen(path, name); },
    onMouseDown: (e) => {
      e.currentTarget.draggable = !(e.target.closest && e.target.closest(_NICHT_ZIEHEN));
    },
    onDragStart: (e) => {
      if (e.target !== e.currentTarget && e.target.closest && e.target.closest(_NICHT_ZIEHEN)) {
        e.preventDefault();
        return;
      }
      if (!dateiInsZiehen(e, path, name)) e.preventDefault();
    },
  };
}

// Datei über den System-Teilen-Dialog teilen (Web Share Level 2).
// Fallback: normaler Download, wenn der Browser Datei-Sharing nicht kann.
export async function shareFile(path, filename) {
  const name = filename || path.split("/").pop() || "datei";
  try {
    const resp = await fetch(downloadUrl(path));
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const file = new File([blob], name, { type: guessMime(name) });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      // NUR die Datei teilen. Mit zusätzlichem `title` legt „Kopieren" (Safari)
      // Datei UND Titeltext in die Zwischenablage → beim Einfügen kam das Dokument
      // doppelt an (gleiches Muster wie SIJ-666 beim Herausziehen).
      await navigator.share({ files: [file] });
      return true;
    }
    // Fallback: Download anstoßen
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return false;
  } catch (e) {
    if (e && e.name === "AbortError") return true; // Nutzer hat Teilen-Dialog geschlossen
    console.warn("[dragOut] Teilen fehlgeschlagen:", e);
    return false;
  }
}

// Rechtsklick-Handler: kleines natives Menü über confirm ist hässlich —
// stattdessen direkt der Teilen-Dialog (macOS Share Sheet in Chrome).
export function contextShareProps(path, filename) {
  if (!path) return {};
  return {
    onContextMenu: (e) => {
      // Nur abfangen, wenn Teilen möglich ist — sonst normales Browser-Menü
      if (!navigator.canShare) return;
      e.preventDefault();
      shareFile(path, filename);
    },
  };
}
